import {
  DuplicationMatchResult,
  InlineComment,
  InlineEditItem,
  ScannerError,
  ScannerErrorType,
} from '@amzn/awscat-pattern-library-service-model';

import { isCommentResolved } from '../components/v2-common/utils/commentStateUtils';

export type ScannerStatus = 'success' | 'failed';

/** User-facing reason that can safely be shown for a failed scanner. */
export type ScannerFailureReason = 'guardrail';

/** The duplication row's distinct display states in the findings table. */
export type DuplicationFindingState = 'hasResults' | 'noResults' | 'failed';

/** Per-scanner aggregated finding count shown in the state pane. */
export interface ScannerFindingsSummary {
  readonly scannerName: string;
  readonly findingsCount: number;
  readonly status: ScannerStatus;
  readonly failureReason?: ScannerFailureReason;
  /** Set only on the duplication row, sourced from the duplication-results endpoint. */
  readonly duplicationState?: DuplicationFindingState;
}

/** Similarity score (0-100) at or above which a duplication match is flagged. */
export const FLAGGED_THRESHOLD = 60;

/** Maximum number of duplication matches shown in the results modal. */
export const MAX_MODAL_RESULTS = 5;

/** The duplication scanner's user-facing display name. */
export const DUPLICATION_SCANNER_NAME = 'Duplication check';

/** Workflow stages that affect which scanners are shown. */
export type ScanStage = 'publisherReview' | 'other';

/** Input shape for computing scanner findings from any scan result source. */
export interface ScanFindingsInput {
  readonly inlineEdits: readonly InlineEditItem[];
  readonly scanComments: readonly InlineComment[];
  readonly scanErrors: readonly ScannerError[];
  readonly stage?: ScanStage;
}

/** Raw API scanner-agent name for the best-practices scanner. */
export const BEST_PRACTICE_SCANNER_NAME = 'BestPracticeScanAgent';

/** Raw API scanner-agent name for the technical-accuracy-and-depth scanner. */
export const TECH_ACCURACY_SCANNER_NAME = 'TechnicalAccuracyAndDepthScanAgent';

/** Raw API scanner-agent name for the duplication scanner. */
export const DUPLICATION_SCANNER_AGENT_NAME = 'DuplicationScanAgent';

const SCANNER_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  [BEST_PRACTICE_SCANNER_NAME]: 'Best practices',
  [TECH_ACCURACY_SCANNER_NAME]: 'Tech accuracy & depth',
  [DUPLICATION_SCANNER_AGENT_NAME]: 'Duplication check',
};

const PUBLISHER_REVIEW_ONLY_SCANNERS: ReadonlySet<string> = new Set(['Duplication check']);

function getKnownScanners(stage: ScanStage): readonly string[] {
  const all = Object.values(SCANNER_DISPLAY_NAMES);
  if (stage === 'publisherReview') return all;
  return all.filter((name) => !PUBLISHER_REVIEW_ONLY_SCANNERS.has(name));
}

/** Maps an API scanner name to its user-facing display name. */
export function toScannerDisplayName(apiName: string): string {
  return SCANNER_DISPLAY_NAMES[apiName] ?? apiName;
}

/** Aggregates unresolved inline edits and scan comments into per-scanner finding counts. */
export function computeScannerFindings(
  input: ScanFindingsInput,
): readonly ScannerFindingsSummary[] {
  const knownScanners = getKnownScanners(input.stage ?? 'other');
  const counts = new Map<string, number>();

  for (const edit of input.inlineEdits) {
    if (edit.resolved) continue;
    const raw = edit.sourceAttributions?.[0]?.sourceScanner?.scannerName ?? 'Unknown';
    const name = toScannerDisplayName(raw);
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }

  for (const comment of input.scanComments) {
    if (isCommentResolved(comment.state)) continue;
    const raw = comment.scannerMetadata?.scannerName ?? 'Unknown';
    const name = toScannerDisplayName(raw);
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }

  for (const name of knownScanners) {
    if (!counts.has(name)) {
      counts.set(name, 0);
    }
  }

  const hasErrors = input.scanErrors.length > 0;
  const isGuardrailFailure = input.scanErrors.some(
    (error) => error.errorType === ScannerErrorType.GUARDRAIL_INTERVENED,
  );

  return Array.from(counts.entries()).map(([scannerName, findingsCount]) => {
    const status: ScannerStatus =
      hasErrors && findingsCount === 0 && knownScanners.includes(scannerName)
        ? 'failed'
        : 'success';
    return {
      scannerName,
      findingsCount,
      status,
      ...(status === 'failed' && isGuardrailFailure && { failureReason: 'guardrail' as const }),
    };
  });
}

/** Scales a 0-1 similarity score up to the rounded 0-100 scale used by the duplication UI. */
export function normalizeSimilarityScore(score: number): number {
  return Math.round(score * 100);
}

/** Returns flagged duplication matches (similarityScore >= 60), highest first, capped at 5. */
export function getTopDuplicationMatches(
  matches: readonly DuplicationMatchResult[],
): readonly DuplicationMatchResult[] {
  return matches
    .filter((match) => match.similarityScore >= FLAGGED_THRESHOLD)
    .slice()
    .sort((a, b) => b.similarityScore - a.similarityScore)
    .slice(0, MAX_MODAL_RESULTS);
}
